import usePermissions, {
  ATHLYN_ADMIN_ROLE,
  ATHLYN_TEAM_ROLE,
} from "@/hooks/usePermissions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ShieldAlert } from "lucide-react";

/** Roles allowed to administer Social Feed catalogs (internal Athlyn staff only). */
export const FEED_ADMIN_ROLES = [ATHLYN_ADMIN_ROLE, ATHLYN_TEAM_ROLE] as const;

export function useIsFeedAdmin(): boolean {
  // Share the single role source with the rest of the portal's permission model.
  const { isAthlynAdmin, isAthlynTeam } = usePermissions();
  return isAthlynAdmin || isAthlynTeam;
}

interface FeedAdminGuardProps {
  children: React.ReactNode;
}

/**
 * Restricts Social Feed catalog pages (Stickers, Kudos Types) to internal
 * Athlyn roles. Non-privileged users see an access-denied notice instead.
 */
export default function FeedAdminGuard({ children }: FeedAdminGuardProps) {
  const isFeedAdmin = useIsFeedAdmin();

  if (!isFeedAdmin) {
    return (
      <div className="flex-1 p-6" data-testid="feed-admin-denied">
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access restricted</AlertTitle>
          <AlertDescription>
            You need the AthlynAdmin or AthlynTeam role to manage Social Feed
            content.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
